import { useState } from "react";
import { ArrowLeft, Check, MessageCircle, Image, Zap, Crown, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition";
import { BottomNavigation } from "@/components/BottomNavigation";
import { MenuDrawer } from "@/components/MenuDrawer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CheckoutDrawer from "@/components/CheckoutDrawer";

interface Plan {
  price: string;
  period: string;
  title: string;
  icon: React.ReactNode;
  features: string[];
  popular?: boolean;
  gradient?: string;
  discountLabel?: string;
}

const plans: Plan[] = [
  {
    price: "0,99€",
    period: "al mese",
    title: "Base",
    icon: <MessageCircle className="w-6 h-6" />,
    features: [
      "Chat di messaggistica con il servizio Tecly",
      "Supporto per domande, curiosità o problemi",
      "Tempi di risposta standard",
    ],
    discountLabel: "Sconti Bassi (5%)",
    gradient: "from-slate-400/15 via-slate-400/5 to-transparent",
  },
  {
    price: "1,99€",
    period: "al mese",
    title: "Plus",
    icon: <Zap className="w-6 h-6" />,
    features: [
      "Chat di messaggistica Tecly",
      "Priorità superiore rispetto al piano Base",
      "Risposte più rapide",
    ],
    discountLabel: "Sconti Medi (10%)",
    gradient: "from-cyan-500/15 via-cyan-500/5 to-transparent",
  },
  {
    price: "2,99€",
    period: "al mese",
    title: "Pro",
    icon: <Image className="w-6 h-6" />,
    features: [
      "Chat di messaggistica Tecly",
      "Accesso esclusivo alla sezione 'Sfondi Pro'",
      "Priorità più alta rispetto ai piani precedenti",
      "Contenuti esclusivi",
    ],
    popular: true,
    discountLabel: "Sconti Alti (15%)",
    gradient: "from-primary/20 via-primary/10 to-transparent",
  },
  {
    price: "3,99€",
    period: "al mese",
    title: "Ultimate",
    icon: <Crown className="w-6 h-6" />,
    features: [
      "Il piano definitivo",
      "Chat con priorità assoluta",
      "Accesso completo agli 'Sfondi Pro'",
      "Tutte le funzionalità incluse",
      "Supporto VIP",
    ],
    discountLabel: "Sconti Massimi (20%)",
    gradient: "from-amber-500/20 via-amber-500/10 to-transparent",
  },
];

const Abbonamenti = () => {
  const navigate = useNavigate();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSelectPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setIsCheckoutOpen(true);
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-24">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-muted transition-all duration-300 hover:scale-110 active:scale-90"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Abbonamenti</h1>
          </div>
        </header>

        {/* Content */}
        <main className="container mx-auto px-4 py-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Scegli il piano perfetto per te</span>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Piani di Abbonamento
            </h2>
            <p className="text-muted-foreground">
              Sblocca funzionalità esclusive con i nostri abbonamenti
            </p>
          </div>

          {/* Plans Grid */}
          <div className="flex flex-col gap-4 max-w-md mx-auto">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl bg-card/80 backdrop-blur-sm ${
                  plan.popular
                    ? "border-primary ring-2 ring-primary/20 shadow-lg shadow-primary/10"
                    : "border-border/50 hover:border-primary/30"
                }`}
              >
                {/* Gradient Background */}
                {plan.gradient && (
                  <div className={`absolute inset-0 bg-gradient-to-br ${plan.gradient} pointer-events-none`} />
                )}
                
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-bl-xl">
                    Popolare
                  </div>
                )}
                <CardHeader className="pb-2 relative">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${plan.popular ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'} transition-all duration-300`}>
                      {plan.icon}
                    </div>
                    <CardTitle className="text-lg">{plan.title}</CardTitle>
                  </div>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">
                      {plan.price}
                    </span>
                    <span className="text-muted-foreground ml-2">
                      / {plan.period}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 relative">
                  <ul className="space-y-2.5">
                    {plan.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-start gap-2.5 text-sm text-muted-foreground"
                      >
                        <div className="mt-0.5 p-0.5 rounded-full bg-primary/10">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Discount Label */}
                  {plan.discountLabel && (
                    <p className="text-xs text-center text-muted-foreground italic">
                      Sconti esclusivi sui prodotti: {plan.discountLabel}
                    </p>
                  )}
                  
                  {/* Sfondi Pro Badge - Show for all plans */}
                  <div className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-gradient-to-r from-yellow-500/10 via-amber-500/10 to-yellow-600/10 border border-yellow-500/20">
                    <Crown className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Accesso completo agli Sfondi Pro</span>
                  </div>
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full py-3.5 px-4 font-medium rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                      plan.popular
                        ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20"
                        : "bg-muted hover:bg-muted/80 text-foreground border border-border/50"
                    }`}
                  >
                    Seleziona Piano
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>

        {/* Checkout Drawer */}
        <CheckoutDrawer
          isOpen={isCheckoutOpen}
          onClose={() => setIsCheckoutOpen(false)}
          selectedPlan={selectedPlan}
        />

        {/* Bottom Navigation */}
        <BottomNavigation onMenuClick={() => setIsMenuOpen(true)} />

        {/* Menu Drawer */}
        <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
      </div>
    </PageTransition>
  );
};

export default Abbonamenti;
